import React, { useState } from 'react';
import AppsContainer from '@tenant/core/AppsContainer';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppSearch from '@tenant/core/AppSearchBar';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import RefreshIcon from '@mui/icons-material/Refresh';
import useModal from '@tenant/utility/hooks/useModal';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@tenant/utility/constants/default';
import { useSelector } from 'react-redux';

import useGetListWebhookRegistrations from 'redux/slices/webhookRegistration/hooks/useGetListWebhookRegistrations';
import useCreateWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useCreateWebhookRegistration';
import useDisableWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useDisableWebhookRegistration';
import useEnableWebhookRegistration from 'redux/slices/webhookRegistration/hooks/useEnableWebhookRegistration';

import WebhookRegistrationForm from './components/WebhookRegistrationForm';
import DisableConfirmDialog from './components/DisableConfirmDialog';
import WebhookRegistrationDetail from './components/WebhookRegistrationDetail';
import { getExtensionPointLabel } from 'constants/ExtensionPointEnum';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

const WebhookRegistrationList = () => {
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchTermChanged, setIsSearchTermChanged] = useState(false);
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [selectedForDisable, setSelectedForDisable] = useState(null);
  const [selectedForDetail, setSelectedForDetail] = useState(null);

  const { visible: addVisible, onClose: onCloseAdd, onShow: onShowAdd } = useModal();
  const { visible: disableVisible, onClose: onCloseDisable, onShow: onShowDisable } = useModal();
  const { visible: detailVisible, onClose: onCloseDetail, onShow: onShowDetail } = useModal();

  const nextTick = useNextTick();

  const { isLoading, isRefetching, refetch } = useGetListWebhookRegistrations({
    pageSize: PAGE_SIZE,
    pageIndex,
    searchTerm,
    isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
  });

  const loading = isLoading || isRefetching;
  const { list } = useSelector((s) => s.webhookRegistration);
  const rows = list?.Items ?? [];
  const totalCount = list?.TotalCount ?? 0;

  const createMutate = useCreateWebhookRegistration();
  const disableMutate = useDisableWebhookRegistration();
  const enableMutate = useEnableWebhookRegistration();

  const canCreate = useAllowedAccess([AuthorizePermission.WEBHOOK_REGISTRATION_CREATE]);
  const canUpdate = useAllowedAccess([AuthorizePermission.WEBHOOK_REGISTRATION_UPDATE]);

  const clearSearchTerm = async () => {
    setSearchTerm('');
    await nextTick();
    refetch();
  };

  const onChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    setPageIndex(DEFAULT_PAGE_INDEX);
    setIsSearchTermChanged(true);
  };

  const refreshTableAction = () => {
    if (pageIndex === DEFAULT_PAGE_INDEX) {
      refetch();
      return;
    }
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const handleShowDisable = (item) => {
    setSelectedForDisable(item);
    onShowDisable();
  };

  const handleConfirmDisable = () => {
    disableMutate.mutate(selectedForDisable.Id, {
      onSuccess: () => {
        onCloseDisable();
        setSelectedForDisable(null);
        refetch();
      },
    });
  };

  const handleEnable = (item) => {
    enableMutate.mutate(item.Id, {
      onSuccess: () => refetch(),
    });
  };

  const handleRowClick = (params) => {
    setSelectedForDetail(params.row);
    onShowDetail();
  };

  const handleCreate = (data) => {
    createMutate.mutate(data, {
      onSuccess: () => {
        onCloseAdd();
        refetch();
      },
    });
  };

  const columns = [
    {
      field: 'EndpointUrl',
      headerName: 'Endpoint URL',
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography
            variant='body2'
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'ExtensionPointId',
      headerName: 'Event',
      flex: 1,
      sortable: false,
      renderCell: (params) => getExtensionPointLabel(params.value),
    },
    {
      field: 'AppIconUrl',
      headerName: 'Logo',
      width: 75,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.IsAppSpecific) return null;
        return (
          <Box
            component='img'
            sx={{ maxHeight: 50, maxWidth: 50, p: 1 }}
            alt={params.row.AppName ?? ''}
            src={params.value}
            onError={(e) => {
              e.target.src = '/assets/images/apps/app-empty-state.svg';
            }}
          />
        );
      },
    },
    {
      field: 'AppName',
      headerName: 'App',
      width: 130,
      sortable: false,
      renderCell: (params) =>
        params.row.IsAppSpecific ? (params.row.AppName ?? '-') : 'All Apps',
    },
    {
      field: 'HttpMethod',
      headerName: 'HTTP Method',
      width: 110,
      sortable: false,
    },
    {
      field: 'IsActive',
      headerName: 'Status',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Disabled'}
          color={params.value ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    {
      field: 'CreatedAt',
      headerName: 'Created At',
      width: 170,
      sortable: false,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleString() : '-',
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      type: 'actions',
      align: 'right',
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            key='action-view'
            label='View / Edit'
            showInMenu
            onClick={() => {
              setSelectedForDetail(params.row);
              onShowDetail();
            }}
          />,
        ];

        if (canUpdate) {
          if (params.row.IsActive) {
            actions.push(
              <GridActionsCellItem
                key='action-disable'
                label='Disable'
                showInMenu
                onClick={() => handleShowDisable(params.row)}
              />,
            );
          } else {
            actions.push(
              <GridActionsCellItem
                key='action-enable'
                label='Re-enable'
                showInMenu
                onClick={() => handleEnable(params.row)}
              />,
            );
          }
        }

        return actions;
      },
    },
  ];

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Deployment</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.WEBHOOK_REGISTRATION.LIST}>
              Webhook Registrations
            </Link>
          </Typography>
        </Breadcrumbs>
      }
    >
      <Box
        sx={{
          '& .MuiDataGrid-root': {
            borderLeft: 0,
            borderRight: 0,
            borderTop: 0,
            borderRadius: 0,
          },
        }}
      >
        <AppsHeader>
          <Box
            flex={1}
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            gap={2}
          >
            <Box display='flex' alignItems='center' gap={2}>
              <AppSearch
                isClearable
                searchValue={searchTerm}
                onClearSearch={clearSearchTerm}
                iconPosition='right'
                id='webhook-search'
                size='small'
                overlap={false}
                placeholder='Search by endpoint URL…'
                onChange={onChangeSearch}
                onBlur={() => {
                  isSearchTermChanged && refetch();
                  setIsSearchTermChanged(false);
                }}
              />
              <FormControl size='small' sx={{ minWidth: 130 }}>
                <InputLabel id='status-filter-label'>Status</InputLabel>
                <Select
                  labelId='status-filter-label'
                  label='Status'
                  value={isActiveFilter}
                  onChange={(e) => {
                    setIsActiveFilter(e.target.value);
                    setPageIndex(DEFAULT_PAGE_INDEX);
                  }}
                >
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='true'>Active</MenuItem>
                  <MenuItem value='false'>Disabled</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display='flex' alignItems='center' gap={1}>
              <AllowedAccess permissionIds={[AuthorizePermission.WEBHOOK_REGISTRATION_CREATE]}>
                <Button variant='outlined' onClick={onShowAdd}>
                  Add New
                </Button>
              </AllowedAccess>
            </Box>
          </Box>
        </AppsHeader>

        <DataGrid
          hideFooter
          autoHeight
          disableColumnSelector
          disableColumnMenu
          rows={rows}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          onRowClick={handleRowClick}
          sx={{ cursor: 'pointer' }}
        />

        <FooterWrapper className='footer'>
          <Box justifyContent='center' className='footerContainer'>
            <Pagination
              count={Math.ceil(totalCount / PAGE_SIZE) || 1}
              page={pageIndex}
              onChange={(e, v) => setPageIndex(v)}
              showFirstButton
              showLastButton
            />
            <Typography>Total: {totalCount}</Typography>
            <Tooltip title='Refresh'>
              <IconButton
                disabled={isRefetching}
                size='small'
                sx={{ ml: 2 }}
                onClick={refreshTableAction}
              >
                <RefreshIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          </Box>
        </FooterWrapper>
      </Box>

      <WebhookRegistrationForm
        visible={addVisible}
        loading={createMutate.isPending}
        handleClose={onCloseAdd}
        handleSubmit={handleCreate}
      />

      <DisableConfirmDialog
        open={disableVisible}
        loading={disableMutate.isPending}
        onConfirm={handleConfirmDisable}
        onCancel={() => {
          onCloseDisable();
          setSelectedForDisable(null);
        }}
      />

      <WebhookRegistrationDetail
        open={detailVisible}
        item={selectedForDetail}
        onClose={() => {
          onCloseDetail();
          setSelectedForDetail(null);
          refetch();
        }}
      />
    </AppsContainer>
  );
};

export default React.memo(WebhookRegistrationList);
